import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Star, Layers, Crown, Lock, AlertTriangle } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { useAuth } from '@/contexts/AuthContext';
import { hasQuotePermission } from '@/lib/quotePermissions';
import { QuotaBadge } from '@/components/quotes/QuotaBadge';
import { AIDraftPanel } from '@/components/quotes/AIDraftPanel';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatMinor, majorToMinor, QUOTE_CURRENCIES } from '@/lib/quoteMoney';
import { trackFirstQuoteCreated } from '@/lib/analytics';
import { toUiItem, toApiItem, applyItemPatch, isPriceMissing } from './quoteItemMapper';
import { MasterCatalogPicker } from './MasterCatalogPicker';
import { MasterVariantSelect } from './MasterVariantSelect';
import { SecurityOptionalMasterItems } from './SecurityOptionalMasterItems';

const API_URL = process.env.REACT_APP_API_URL;
const isNew = (id) => !id || id === 'new';

const DEFAULT_PACKAGES = [
  { key: 'economic', name: 'Ekonomik', is_recommended: false, items: [] },
  { key: 'recommended', name: 'Önerilen', is_recommended: true, items: [] },
  { key: 'premium', name: 'Premium', is_recommended: false, items: [] },
];

// Localised default package name for the single-mode package.
function singleName(t) { return t('form.packageNameSingle'); }
// Localised default package names for multi mode. Package NAMES are
// persisted server-side (snapshot), so we only localise them when they
// still match the default. User-edited names are left untouched.
function defaultPackageName(t, key) {
  switch (key) {
    case 'economic': return t('form.packageEconomic');
    case 'recommended': return t('form.packageRecommended');
    case 'premium': return t('form.packagePremium');
    default: return t('form.packageNameSingle');
  }
}

function emptyItem() {
  return {
    catalog_item_id: null,
    item_type: 'product',
    name: '',
    description: '',
    unit: '',
    quantity: 1,
    unit_price_major: '',
    cost_price_major: '',
    line_discount_percent: 0,
  };
}

/** UI ↔ API converters + patch normalizer moved to `./quoteItemMapper`
 *  so the price_unset regression suite can import them without pulling
 *  the React tree. See tests: __tests__/QuoteForm.priceUnset.test.js
 */

export function QuoteForm() {
  const { t, i18n } = useTranslation('quotes');
  const { user } = useAuth();
  const showCosts = hasQuotePermission(user, 'costs');
  const lang = (i18n.language || 'tr').split('-')[0];
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !isNew(id);

  // Entitlement check — only relevant when creating a new quote. Editing
  // an existing quote or downloading its PDF is always allowed even after
  // the monthly limit is exhausted.
  const { entitlement, isPro, loading: entLoading } = useQuoteEntitlement();
  const quoteQuotaExhausted = !editing && entitlement?.limits?.quote_count != null
    && (entitlement.usage?.quote_count ?? 0) >= entitlement.limits.quote_count;

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [industries, setIndustries] = useState({});
  const [profileIndustry, setProfileIndustry] = useState('');
  const [pdfOptions, setPdfOptions] = useState({ templates: [], palettes: [] });
  const [pdfTemplate, setPdfTemplate] = useState('corporate_v2');
  const [pdfPalette, setPdfPalette] = useState('light_blue');

  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [status, setStatus] = useState('draft');

  // P2 pricing
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [taxRate, setTaxRate] = useState(0);
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [globalDiscountPct, setGlobalDiscountPct] = useState(0);

  // For "single" mode, we track a single package internally too.
  const [packages, setPackages] = useState([
    { key: 'single', name: singleName(t), is_recommended: false, items: [], totals: null },
  ]);
  // Currently focused package tab (multi-mode only).
  const [activePkgIdx, setActivePkgIdx] = useState(0);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);

  // ------------- data load ----------------------
  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes, iRes, pdfRes] = await Promise.all([
          axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } }),
          axios.get(`${API_URL}/api/quote/customers`, { params: { is_active: true, page_size: 200 } }),
          axios.get(`${API_URL}/api/quote/industries`).catch(() => ({ data: { industries: [] } })),
          axios.get(`${API_URL}/api/quote/profile/pdf-options`).catch(() => ({ data: { templates: [], palettes: [] } })),
        ]);
        setCustomers(cRes.data.items || []);
        setCurrency(pRes.data.currency || 'TRY');
        // New quotes start with the business's default tax rate (country
        // preset unless changed in Quote Settings); edits keep their own.
        if (!editing) setTaxRate(Number(pRes.data.default_tax_rate_percent || 0));
        setProfileIndustry(pRes.data.industry || '');
        setPdfOptions({
          templates: pdfRes.data?.templates || [],
          palettes: pdfRes.data?.palettes || [],
        });
        setPdfTemplate(pRes.data.pdf_template || 'corporate_v2');
        setPdfPalette(pRes.data.pdf_palette || 'light_blue');
        const indMap = {};
        (iRes.data.industries || []).forEach((ind) => {
          indMap[ind.key] = lang === 'en' ? (ind.name_en || ind.name) : ind.name;
        });
        setIndustries(indMap);
        if (editing) {
          const { data } = await axios.get(`${API_URL}/api/quote/quotes/${id}`);
          setTitle(data.title || '');
          setCustomerId(data.customer_id || '');
          setNotes(data.notes || '');
          setTerms(data.terms || '');
          setValidUntil(data.valid_until || '');
          setCurrency(data.currency || 'TRY');
          setStatus(data.status || 'draft');
          setMode(data.mode || 'single');
          setTaxRate(Number(data.tax_rate_percent || 0));
          setTaxIncluded(Boolean(data.tax_included));
          setGlobalDiscountPct(Number(data.global_discount_percent || 0));
          setPdfTemplate(data.pdf_template_snapshot || 'corporate_v2');
          setPdfPalette(data.pdf_palette_snapshot || 'light_blue');
          const pkgs = (data.packages || []).map((p) => ({
            key: p.key || 'single',
            name: p.name || singleName(t),
            is_recommended: !!p.is_recommended,
            items: (p.items || []).map(toUiItem),
            totals: p.totals || null,
          }));
          setPackages(pkgs.length ? pkgs : packages);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Switching mode reshapes packages (keeping existing items where possible).
  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === 'single') {
      // Merge — keep the recommended (or first) package's items.
      const src = packages.find((p) => p.is_recommended) || packages[0];
      setPackages([{ key: 'single', name: singleName(t), is_recommended: false, items: src?.items || [], totals: null }]);
      setActivePkgIdx(0);
    } else {
      // Expand — keep the current single package as "Önerilen".
      const existing = packages[0]?.items || [];
      setPackages(
        DEFAULT_PACKAGES.map((base, idx) => ({
          ...base,
          name: defaultPackageName(t, base.key),
          items: idx === 1 ? existing : [],
        })),
      );
      setActivePkgIdx(1); // focus recommended
    }
    setMode(nextMode);
  };

  // ------------- AI Draft (Package 5) - apply handler ----------------
  const applyAIDraft = (draft) => {
    if (!draft || !Array.isArray(draft.packages) || draft.packages.length === 0) return;
    // Başlık boşsa doldur, doluysa dokunma.
    if (!title.trim() && draft.title) setTitle(draft.title);
    if (draft.notes) setNotes((prev) => (prev ? `${prev}\n\n${draft.notes}` : draft.notes));
    if (draft.currency) setCurrency(draft.currency);

    const nextMode = draft.package_mode === 'tiered' ? 'multi' : 'single';
    setMode(nextMode);

    const uiPackages = draft.packages.map((p, idx) => ({
      key: p.key || (nextMode === 'single' ? 'single' : DEFAULT_PACKAGES[idx]?.key),
      name: p.name || (nextMode === 'single' ? singleName(t) : defaultPackageName(t, DEFAULT_PACKAGES[idx]?.key)),
      is_recommended: !!p.is_recommended,
      items: (p.items || []).map(toUiItem),
      totals: null,
    }));
    setPackages(uiPackages.length ? uiPackages : packages);
    // Recommended paketi (varsa) aktif tab yap.
    const recIdx = uiPackages.findIndex((p) => p.is_recommended);
    setActivePkgIdx(recIdx >= 0 ? recIdx : 0);
  };

  // ------------- item helpers ----------------
  const activePkg = packages[activePkgIdx] || packages[0];

  // Price check: compare each line with this business's own recent prices
  // for the same item. Debounced; never suggests a price, only flags typos
  // and unusually low/high prices.
  const [priceChecks, setPriceChecks] = useState({});
  const priceCheckPayload = useMemo(() => {
    const items = (activePkg?.items || [])
      .map((it, idx) => {
        const raw = String(it.unit_price_major ?? '').trim();
        if (!raw || !it.name?.trim()) return null;
        return {
          ref: String(idx),
          catalog_item_id: it.catalog_item_id || null,
          master_variant_id: it.master_variant_id || null,
          name: it.name.trim().slice(0, 200),
          unit_price_minor: majorToMinor(raw),
        };
      })
      .filter(Boolean);
    return items.length ? JSON.stringify({ currency, items, exclude_quote_id: editing ? id : null }) : '';
  }, [activePkg, currency, editing, id]);
  useEffect(() => {
    if (!priceCheckPayload) { setPriceChecks({}); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/quote/insights/price-check`, JSON.parse(priceCheckPayload));
        if (cancelled) return;
        const map = {};
        (data.items || []).forEach((r) => { if (r.flag) map[r.ref] = r; });
        setPriceChecks(map);
      } catch { /* advisory only */ }
    }, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [priceCheckPayload]);
  const missingPriceItems = useMemo(
    () => packages.flatMap((pkg) => (pkg.items || []).filter(isPriceMissing)),
    [packages],
  );
  const missingPriceCount = missingPriceItems.length;
  const missingPriceNames = missingPriceItems
    .map((item) => item.name?.trim())
    .filter(Boolean);
  const missingPricePreview = missingPriceNames.slice(0, 5).join(', ');
  const missingPriceRemaining = Math.max(0, missingPriceNames.length - 5);
  const canEdit = !editing || status === 'draft';
  const updatePackageMeta = (idx, patch) =>
    setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const setPackageItems = (idx, items) =>
    setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, items } : p)));

  const addManualItem = () => {
    setPackageItems(activePkgIdx, [...(activePkg?.items || []), emptyItem()]);
  };

  const addFromMasterCatalog = (src) => {
    setPackageItems(activePkgIdx, [
      ...(activePkg?.items || []),
      {
        catalog_item_id: null,
        master_family_id: src.master_family_id,
        master_variant_id: src.master_variant_id,
        master_catalog_revision: src.master_catalog_revision,
        technical_attributes: src.technical_attributes || {},
        item_type: src.item_type || 'product',
        name: src.name,
        description: src.description || '',
        unit: src.unit || 'adet',
        quantity: 1,
        unit_price_major: src.unit_price_major ?? '',
        cost_price_major: src.cost_price_major ?? '',
        line_discount_percent: 0,
        price_unset: src.price_unset ?? !(src.unit_price_major && Number(src.unit_price_major) > 0),
      },
    ]);
  };

  const updateItem = (itemIdx, patch) => {
    setPackageItems(
      activePkgIdx,
      (activePkg?.items || []).map((it, i) => (i === itemIdx ? applyItemPatch(it, patch) : it)),
    );
  };
  const removeItem = (itemIdx) => {
    setPackageItems(activePkgIdx, (activePkg?.items || []).filter((_, i) => i !== itemIdx));
  };

  // ------------- client-side preview totals ---------
  const previewTotals = useMemo(() => {
    // Match the backend's algorithm at a high level for a live preview.
    const results = packages.map((pkg) => {
      let subtotal = 0, lineDisc = 0, totalCost = 0;
      for (const it of pkg.items) {
        const qty = Number(String(it.quantity || 0).replace(',', '.')) || 0;
        const unit = majorToMinor(it.unit_price_major);
        const cost = majorToMinor(it.cost_price_major || 0);
        const pct = Math.max(0, Math.min(100, Number(it.line_discount_percent) || 0));
        const lsub = Math.round(qty * unit);
        const ldisc = Math.round((lsub * pct) / 100);
        subtotal += lsub;
        lineDisc += ldisc;
        totalCost += Math.round(qty * cost);
      }
      const afterLine = subtotal - lineDisc;
      const gDisc = Math.round((afterLine * Math.max(0, Math.min(100, Number(globalDiscountPct) || 0))) / 100);
      const netNoExtract = Math.max(0, afterLine - gDisc);
      const taxPct = Math.max(0, Number(taxRate) || 0);
      let tax, net, grand;
      if (taxIncluded) {
        tax = taxPct > 0 ? Math.round((netNoExtract * taxPct) / (100 + taxPct)) : 0;
        net = netNoExtract - tax;
        grand = netNoExtract;
      } else {
        tax = Math.round((netNoExtract * taxPct) / 100);
        net = netNoExtract;
        grand = net + tax;
      }
      const profit = net - totalCost;
      const margin = net > 0 ? (profit * 100) / net : 0;
      return { subtotal, lineDisc, gDisc, tax, net, grand, totalCost, profit, margin };
    });
    return results;
  }, [packages, taxRate, taxIncluded, globalDiscountPct]);

  // ------------- submit -------------------
  const submit = async () => {
    if (!title.trim()) return toast.error(t('form.validationTitle'));
    // P4: customer is now OPTIONAL — user can save a customerless draft.
    const hasAnyItems = packages.some((p) => (p.items || []).length > 0);
    if (!hasAnyItems) return toast.error(t('form.validationItems'));

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        ...(!editing ? { quote_language: lang } : {}),
        // Empty string signals "detach" on update; on create it's simply skipped
        // by the backend when null. Never send a bogus id.
        customer_id: customerId || (editing ? '' : null),
        currency,
        notes, terms,
        valid_until: validUntil || null,
        mode,
        tax_rate_percent: Number(taxRate) || 0,
        tax_included: !!taxIncluded,
        global_discount_percent: Number(globalDiscountPct) || 0,
        pdf_template: pdfTemplate,
        pdf_palette: pdfPalette,
        packages: packages.map((p) => ({
          key: p.key || 'single',
          name: p.name || singleName(t),
          is_recommended: !!p.is_recommended,
          items: (p.items || []).map(toApiItem),
        })),
      };
      let saved;
      const isFirstQuote = !editing && (entitlement?.usage?.quote_count ?? 0) === 0;
      if (editing) {
        ({ data: saved } = await axios.put(`${API_URL}/api/quote/quotes/${id}`, payload));
      } else {
        ({ data: saved } = await axios.post(`${API_URL}/api/quote/quotes`, payload));
      }
      if (isFirstQuote && saved?.id) {
        const itemCount = packages.reduce((total, pkg) => total + (pkg.items || []).length, 0);
        trackFirstQuoteCreated({
          quoteId: saved.id,
          language: lang,
          currency,
          mode,
          itemCount,
        });
      }
      toast.success(t('form.savedToast'));
      navigate(`/quotes/${saved.id}`);
    } catch (err) {
      // P3 quota / entitlement errors — steer the user to /quote-upgrade.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && typeof detail === 'object' && detail?.message) {
        toast.error(detail.message, {
          action: {
            label: t('form.quotaExceededCta'),
            onClick: () => navigate('/quote-upgrade'),
          },
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || t('common.error')));
      }
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(t('form.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/quotes/${id}`);
      toast.success(t('form.deletedToast'));
      navigate('/quotes');
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  if (loading || entLoading) {
    return <QuoteLayout><div className="p-8 text-center text-[#595959]">{t('common.loading')}</div></QuoteLayout>;
  }

  // Free plan quote quota exhausted — render an upgrade blocker instead
  // of the form + AI panel. Existing quotes remain viewable/editable (this
  // branch only triggers when `!editing`).
  if (quoteQuotaExhausted) {
    return (
      <QuoteLayout>
        <div className="max-w-2xl mx-auto" data-testid="quote-new-blocked">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate('/quotes')} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1" data-testid="quote-back-btn">
              <ArrowLeft className="w-4 h-4" /> {t('form.back')}
            </button>
            <QuotaBadge />
          </div>
          <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-[#1A1A2E]" data-testid="quote-blocked-title">
              {t('form.blockedTitle')}
            </h1>
            <p className="text-sm text-[#64748B] mt-2" data-testid="quote-blocked-message">
              {t('form.blockedMessage')}
            </p>
            <p className="text-xs text-[#94A3B8] mt-3">
              {t('form.blockedHint')}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/quote-upgrade" data-testid="quote-blocked-upgrade">
                <Button className="bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white gap-2 w-full sm:w-auto">
                  <Crown className="w-4 h-4" /> {t('form.upgradeCta')}
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => navigate('/quotes')}
                data-testid="quote-blocked-back-to-list"
              >
                {t('form.backToList')}
              </Button>
            </div>
          </div>
        </div>
      </QuoteLayout>
    );
  }

  const t2 = previewTotals[activePkgIdx] || {};

  return (
    <QuoteLayout>
      <div className="max-w-5xl mx-auto" data-testid={editing ? 'quote-edit-page' : 'quote-new-page'}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button onClick={() => navigate('/quotes')} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1" data-testid="quote-back-btn">
            <ArrowLeft className="w-4 h-4" /> {t('form.back')}
          </button>
          <QuotaBadge />
        </div>
        <h1 className="text-2xl font-bold text-[#1A1A2E] mb-6">{editing ? t('form.titleEdit') : t('form.titleNew')}</h1>

        {missingPriceCount > 0 && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900" data-testid="missing-price-summary">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">{t('detail.missingPriceTitle', { count: missingPriceCount })}</p>
              <p className="text-sm">
                {missingPricePreview
                  ? t(
                      missingPriceRemaining > 0
                        ? 'detail.missingPriceSummaryMore'
                        : 'detail.missingPriceSummary',
                      { names: missingPricePreview, remaining: missingPriceRemaining },
                    )
                  : t('detail.missingPriceDescription')}
              </p>
            </div>
          </div>
        )}

        {/* AI Draft — Package 5: OpenAI ile otomatik taslak */}
        {!editing && canEdit && (
          <AIDraftPanel
            disabled={saving}
            isPro={isPro}
            industryKey={profileIndustry}
            industryName={industries[profileIndustry] || ''}
            onApplyDraft={applyAIDraft}
          />
        )}

        {/* Quote meta */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t('form.labelTitle')}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canEdit} data-testid="quote-title-input" placeholder={t('form.titlePlaceholder')} />
            </div>
            <div>
              <Label>{t('form.labelCustomer')} <span className="text-[#94A3B8] text-xs">({t('common.optional')})</span></Label>
              <div className="flex gap-2">
                <Select value={customerId} onValueChange={setCustomerId} disabled={!canEdit}>
                  <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder={t('form.selectCustomerPlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {customerId && canEdit && (
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setCustomerId('')}
                    data-testid="quote-customer-clear"
                    title={t('form.clearCustomer')}
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </div>
              {!customerId && (
                <p className="text-[11px] text-[#94A3B8] mt-1">{t('form.noCustomerHint')}</p>
              )}
            </div>
            <div>
              <Label>{t('form.labelValidUntil')}</Label>
              <Input type="date" value={validUntil || ''} onChange={(e) => setValidUntil(e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <Label>{t('form.labelCurrency')}</Label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={!canEdit}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white disabled:opacity-60"
                data-testid="quote-currency-select"
              >
                {currency && !QUOTE_CURRENCIES.some(([code]) => code === currency) && (
                  <option value={currency}>{currency}</option>
                )}
                {QUOTE_CURRENCIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Pricing controls */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4">{t('form.pricingSection')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t('form.modeLabel')}</Label>
              <Select value={mode} onValueChange={switchMode} disabled={!canEdit}>
                <SelectTrigger data-testid="quote-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">{t('form.modeSingle')}</SelectItem>
                  <SelectItem value="multi">{t('form.modeMulti')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('form.taxRate')}</Label>
              <Input type="number" min="0" max="100" step="1" value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)} disabled={!canEdit} data-testid="quote-tax-input" />
            </div>
            <div>
              <Label>{t('form.taxMode')}</Label>
              <Select value={taxIncluded ? 'inc' : 'exc'} onValueChange={(v) => setTaxIncluded(v === 'inc')} disabled={!canEdit}>
                <SelectTrigger data-testid="quote-tax-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exc">{t('form.taxExclusive')}</SelectItem>
                  <SelectItem value="inc">{t('form.taxInclusive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('form.globalDiscount')}</Label>
              <Input type="number" min="0" max="100" step="0.01" value={globalDiscountPct}
                onChange={(e) => setGlobalDiscountPct(e.target.value)} disabled={!canEdit} data-testid="quote-global-discount-input" />
            </div>
          </div>
        </div>

        {/* Multi-package tabs */}
        {mode === 'multi' && (
          <div className="flex gap-2 mb-3" data-testid="quote-package-tabs">
            {packages.map((p, i) => (
              <button
                key={p.key}
                onClick={() => setActivePkgIdx(i)}
                className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 ${
                  activePkgIdx === i
                    ? (p.is_recommended ? 'bg-[#2563EB] text-white ring-2 ring-blue-300' : 'bg-[#1A1A2E] text-white')
                    : (p.is_recommended ? 'bg-blue-50 text-[#2563EB] border border-blue-200' : 'bg-white border border-[#E5E5E5] text-[#475569]')
                }`}
                data-testid={`quote-pkg-tab-${p.key}`}
              >
                {p.is_recommended && <Star className="w-3.5 h-3.5 fill-current" />}
                <Input
                  value={p.name}
                  onChange={(e) => updatePackageMeta(i, { name: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-24 border-0 bg-transparent p-0 text-inherit"
                  disabled={!canEdit}
                />
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 text-xs text-[#94A3B8]">
              <Layers className="w-4 h-4" /> {t('form.activePackage')} <b>{activePkg?.name}</b>
            </div>
          </div>
        )}

        {/* Items for the active package */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A2E]">
              {mode === 'multi' ? t('form.sectionItemsFor', { name: activePkg?.name }) : t('form.sectionItems')}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={catalogPickerOpen ? "secondary" : "outline"}
                onClick={() => setCatalogPickerOpen((open) => !open)}
                disabled={!canEdit}
                data-testid="quote-catalog-picker-toggle"
              >
                <Layers className="w-4 h-4 mr-1" />
                {catalogPickerOpen
                  ? (lang === 'en' ? 'Close catalog' : 'Kataloğu Kapat')
                  : t('form.items.addFromCatalog')}
              </Button>
              <Button variant="outline" onClick={addManualItem} disabled={!canEdit} data-testid="quote-add-manual-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('form.items.addManual')}
              </Button>
            </div>
          </div>

          {catalogPickerOpen && (
            <div className="mb-4" data-testid="quote-master-catalog-area">
              <MasterCatalogPicker
                sectorKey={profileIndustry}
                disabled={!canEdit}
                onAdd={addFromMasterCatalog}
                lang={lang}
              />
            </div>
          )}

          {(activePkg?.items || []).length === 0 ? (
            <div className="text-center py-6 text-[#94A3B8]">{t('form.items.empty')}</div>
          ) : (
            <div className="space-y-3">
              {(activePkg?.items || []).map((it, idx) => {
                const qty = Number(String(it.quantity || 0).replace(',', '.')) || 0;
                const unitMinor = majorToMinor(it.unit_price_major);
                const lineSub = Math.round(qty * unitMinor);
                const pct = Math.max(0, Math.min(100, Number(it.line_discount_percent) || 0));
                const lineDisc = Math.round((lineSub * pct) / 100);
                const lineTotal = lineSub - lineDisc;
                const missing = isPriceMissing({
                  price_unset: it.price_unset,
                  unit_price_minor: it.unit_price_major === '' ? null : unitMinor,
                });
                return (
                  <div key={idx} className={`border rounded-xl p-3 ${missing ? 'border-amber-300 bg-amber-50/40' : 'border-[#F1F5F9]'}`} data-testid={`quote-item-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-4">
                        <Label className="text-xs">{t('form.items.name')}</Label>
                        <Input value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} disabled={!canEdit} />
                        {it.master_family_id && (
                          <div className="mt-2">
                            <Label className="text-[11px] text-[#64748B]">
                              {lang === 'en' ? 'Variant' : 'Varyant'}
                            </Label>
                            <MasterVariantSelect
                              familyId={it.master_family_id}
                              sectorKey={profileIndustry}
                              value={it.master_variant_id}
                              disabled={!canEdit}
                              compact
                              lang={lang}
                              onChange={(patch) => updateItem(idx, patch)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-1">
                        <Label className="text-xs">{t('form.items.unit')}</Label>
                        <Input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} placeholder={t('form.unitDefault')} disabled={!canEdit} />
                      </div>
                      <div className="md:col-span-1">
                        <Label className="text-xs">{t('form.items.quantity')}</Label>
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          value={it.quantity}
                          placeholder={it.quantity_unset ? (lang === 'en' ? 'After survey' : 'Keşif sonrası') : ''}
                          onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                          disabled={!canEdit}
                        />
                        {it.quantity_unset && (
                          <p className="text-[11px] text-amber-700 mt-1">
                            {it.design_note || (lang === 'en'
                              ? 'Quantity will be confirmed after survey.'
                              : 'Miktar keşif sonrası netleştirilecek.')}
                          </p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">{t('form.items.unitPrice')}</Label>
                        <Input type="number" step="0.01" min="0" value={it.unit_price_major} onChange={(e) => updateItem(idx, { unit_price_major: e.target.value })} disabled={!canEdit} />
                        {priceChecks[String(idx)] && (
                          <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1" data-testid={`price-check-${idx}`}>
                            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>
                              {t(priceChecks[String(idx)].flag === 'low' ? 'form.priceCheckLow' : 'form.priceCheckHigh', {
                                median: formatMinor(priceChecks[String(idx)].median_minor, currency),
                                count: priceChecks[String(idx)].samples,
                                pct: Math.abs(Math.round(priceChecks[String(idx)].deviation_percent || 0)),
                              })}
                            </span>
                          </p>
                        )}
                      </div>
                      {showCosts && (
                      <div className="md:col-span-2">
                        <Label className="text-xs">{t('form.cost')}</Label>
                        <Input type="number" step="0.01" min="0" value={it.cost_price_major || ''} placeholder="0.00" onChange={(e) => updateItem(idx, { cost_price_major: e.target.value })} disabled={!canEdit} />
                      </div>
                      )}
                      <div className="md:col-span-1">
                        <Label className="text-xs">{t('form.discountShort')}</Label>
                        <Input type="number" step="0.01" min="0" max="100" value={it.line_discount_percent} onChange={(e) => updateItem(idx, { line_discount_percent: e.target.value })} disabled={!canEdit} />
                      </div>
                      <div className={`md:col-span-1 text-right ${missing ? 'text-amber-700' : ''}`}>
                        <p className="text-xs text-[#94A3B8]">{t('form.lineTotal')}</p>
                        <p className={`font-semibold text-sm ${missing ? 'text-amber-700' : 'text-[#1A1A2E]'}`}>
                          {missing ? t('detail.pricePending') : formatMinor(lineTotal, currency)}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end mt-1">
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={!canEdit}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <SecurityOptionalMasterItems
            sectorKey={profileIndustry}
            existingItems={activePkg?.items || []}
            disabled={!canEdit}
            language={lang}
            onAdd={addFromMasterCatalog}
          />

          {/* Live totals — internal (shows cost + profit) */}
          <div className="mt-6 pt-4 border-t border-[#F1F5F9] grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="quote-live-totals">
            <div>
              <p className="text-xs text-[#94A3B8]">{t('form.totalsSubtotal')}</p>
              <p className="font-semibold">{formatMinor(t2.subtotal || 0, currency)}</p>
            </div>
            <div>
              <p className="text-xs text-[#94A3B8]">{t('form.totalsTax')}</p>
              <p className="font-semibold">{formatMinor(t2.tax || 0, currency)}</p>
            </div>
            <div>
              <p className="text-xs text-[#94A3B8]">{t('form.totalsGrandTotal')}</p>
              <p className="font-bold text-[#2563EB]">{formatMinor(t2.grand || 0, currency)}</p>
            </div>
            {showCosts && (
            <div>
              <p className="text-xs text-emerald-700">{t('form.totalsProfit')}</p>
              <p className="font-semibold text-emerald-700" data-testid="quote-live-profit">
                {formatMinor(t2.profit || 0, currency)} · %{(t2.margin || 0).toFixed(2)}
              </p>
            </div>
            )}
          </div>
          {showCosts && <p className="text-[11px] text-[#94A3B8] mt-2">{t('form.totalsProfitNote')}</p>}
        </div>

        {/* PDF appearance — stored per quote as a snapshot. */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-1">
            {lang === 'en' ? 'PDF appearance' : 'PDF görünümü'}
          </h2>
          <p className="text-xs text-[#94A3B8] mb-4">
            {lang === 'en'
              ? 'Choose how this quote will look before saving. Existing quotes keep their own saved appearance.'
              : 'Bu teklifin PDF görünümünü kaydetmeden önce seçin. Mevcut teklifler kendi kayıtlı görünümünü korur.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{lang === 'en' ? 'Layout' : 'Şablon'}</Label>
              <select
                value={pdfTemplate}
                onChange={(e) => setPdfTemplate(e.target.value)}
                disabled={!canEdit}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                data-testid="quote-pdf-template"
              >
                {(pdfOptions.templates || []).map((opt) => (
                  <option key={opt.key} value={opt.key} disabled={opt.pro_only && !isPro}>
                    {opt.key === 'product_visual'
                      ? (lang === 'en' ? 'With product images' : 'Ürün görselli')
                      : (lang === 'en' ? 'Without product images' : 'Görselsiz kurumsal')}
                    {opt.pro_only && !isPro ? ' (Pro)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>{lang === 'en' ? 'Accent color' : 'Renk'}</Label>
              <select
                value={pdfPalette}
                onChange={(e) => setPdfPalette(e.target.value)}
                disabled={!canEdit}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                data-testid="quote-pdf-palette"
              >
                {(pdfOptions.palettes || []).map((opt) => (
                  <option key={opt.key} value={opt.key} disabled={opt.pro_only && !isPro}>
                    {lang === 'en'
                      ? ({
                          light_blue: 'Light blue', light_orange: 'Light orange',
                          light_pink: 'Light pink', light_purple: 'Light purple',
                          light_green: 'Light green', mono_gray: 'Gray / black',
                        }[opt.key] || opt.name)
                      : opt.name}
                    {opt.pro_only && !isPro ? ' (Pro)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notes + terms */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6 space-y-4">
          <div>
            <Label>{t('form.labelNotes')}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} disabled={!canEdit} />
          </div>
          <div>
            <Label>{t('form.labelTerms')}</Label>
            <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} placeholder={t('form.termsPlaceholder')} rows={2} disabled={!canEdit} />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {editing && (
              <Button variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/quotes')}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={saving || !canEdit} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="quote-save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? t('common.saving') : t('form.saveDraft')}
            </Button>
          </div>
        </div>
      </div>
    </QuoteLayout>
  );
}

export default QuoteForm;
